import { useEffect, useRef } from 'react';

/**
 * useMicroOutageLogger
 * ─────────────────────────────────────────────────────────────────────────
 * Hook INDEPENDIENTE de useStableOnlineStatus (no lo modifica, no comparte
 * estado ni timers con él). Detecta cortes reales de internet -incluidos
 * los de 1-2 s- haciendo ping activo a servicios externos estables, en vez
 * de fiarse de navigator.onLine / eventos 'online'-'offline' del navegador
 * (que solo reflejan si hay interfaz de red activa, no conectividad real).
 *
 * El ping NO toca tu servidor: se hace contra endpoints externos ligeros
 * pensados para esto (connectivity-check). Tu servidor solo se usa para
 * persistir el log cuando se confirma una recuperación (onFlush), igual
 * que ya hacías con doSaveOfflineToDb.
 *
 * Persistencia:
 * - El inicio de un corte se guarda en localStorage EN EL MOMENTO en que
 *   se detecta (no se espera a que vuelva internet), para sobrevivir a un
 *   reload/cierre de pestaña durante el corte.
 * - Al montar, si queda un corte "abierto" de una sesión anterior (la app
 *   se recargó/cerró mientras no había internet), se cierra y se intenta
 *   enviar en cuanto hay conectividad.
 * - Si el envío a BD falla, el log queda en localStorage y se reintenta
 *   en cada ciclo posterior en el que haya internet, no solo una vez.
 *
 * @param {Object} opts
 * @param {number}   opts.intervalMs      Frecuencia de comprobación (default 3000ms)
 * @param {number}   opts.timeoutMs       Timeout por ping (default 1500ms)
 * @param {string}   opts.logsKey         Clave localStorage: array de cortes cerrados pendientes de enviar
 * @param {string}   opts.openOutageKey   Clave localStorage: corte en curso (si existe)
 * @param {function} opts.onFlush         async (strLogsJson) => boolean. Persiste en tu BD.
 */
const PING_TARGETS = [
  'https://www.gstatic.com/generate_204', // endpoint oficial de connectivity-check de Google
  'https://www.cloudflare.com/cdn-cgi/trace', // fallback, otro proveedor distinto
];

const useMicroOutageLogger = ({
  intervalMs = 3000,
  timeoutMs = 1500,
  logsKey = 'MxMain.inet_outage_logs',
  openOutageKey = 'MxMain.inet_outage_open',
  onFlush,
} = {}) => {
  const isDownRef = useRef(false);
  const downSinceRef = useRef(null);
  const intervalRef = useRef(null);

  // ── Helpers de localStorage ──────────────────────────────────────────
  const markOutageStart = (strStart) => {
    localStorage.setItem(openOutageKey, strStart);
  };

  const clearOutageStart = () => {
    localStorage.removeItem(openOutageKey);
  };

  const appendClosedLog = (obLog) => {
    const strLogs = localStorage.getItem(logsKey) || '[]';
    const arLogs = JSON.parse(strLogs);
    arLogs.push(obLog);
    localStorage.setItem(logsKey, JSON.stringify(arLogs));
  };

  const flushLogs = async () => {
    const strLogs = localStorage.getItem(logsKey);
    if (!strLogs || strLogs === '[]' || !onFlush) return;
    try {
      const boOk = await onFlush(strLogs);
      if (boOk) localStorage.removeItem(logsKey);
    } catch (e) {
      // se reintenta en el próximo ciclo, el log sigue en localStorage
    }
  };

  const closeOutage = (strStart, strEnd, boApproxEnd = false) => {
    appendClosedLog({
      process: 'microOutage',
      dtStart: strStart,
      dtEnd: strEnd,
      durationMs: new Date(strEnd) - new Date(strStart),
      boApproxEnd, // true si el corte se cerró tras un reload y no sabemos el instante exacto de recuperación
    });
    clearOutageStart();
  };

  // true si al menos uno de los targets responde (aunque sea opaco por no-cors)
  const checkConnectivity = async () => {
    const attempts = PING_TARGETS.map((url) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      return fetch(url, { method: 'GET', mode: 'no-cors', cache: 'no-store', signal: controller.signal })
        .then(() => { clearTimeout(timer); return true; })
        .catch(() => { clearTimeout(timer); return false; });
    });

    const results = await Promise.all(attempts);
    return results.some((boOk) => boOk === true);
  };

  const tick = async () => {
    const boOnline = await checkConnectivity();

    if (boOnline) {
      // Transición offline -> online
      if (isDownRef.current) {
        isDownRef.current = false;
        const strNow = new Date().toISOString();
        closeOutage(downSinceRef.current, strNow, false);
        downSinceRef.current = null;
      }
      // reintenta enviar cualquier log pendiente (de este corte o de uno anterior fallido)
      flushLogs();
    } else {
      // Transición online -> offline (solo se marca la primera vez)
      if (!isDownRef.current) {
        isDownRef.current = true;
        const strNow = new Date().toISOString();
        downSinceRef.current = strNow;
        markOutageStart(strNow); // persistido YA, no se espera a que vuelva internet
      }
    }
  };

  useEffect(() => {
    // Al montar: ¿quedó un corte abierto de una sesión anterior (reload/cierre durante el corte)?
    const strPendingStart = localStorage.getItem(openOutageKey);
    if (strPendingStart) {
      isDownRef.current = true;
      downSinceRef.current = strPendingStart;
    }

    // Intenta enviar de entrada cualquier log cerrado que quedara pendiente por fallo previo de onFlush
    flushLogs();

    intervalRef.current = setInterval(tick, intervalMs);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, timeoutMs]);
};

export default useMicroOutageLogger;
